import type { Editor } from '@tiptap/vue-3';
import type { Ref } from 'vue';
import { readonly, ref, watch } from 'vue';

/**
 * Live counter maps for numbered document elements.
 *
 * Scans the ProseMirror document on every editor update (300 ms debounce)
 * and builds an ordered Map<nodeId, sequentialNumber> for each element type.
 *
 * Usage in EditorCanvas:
 *   const { tableCounters, figureCounters } = useDocumentCounters(editor);
 *   provide('editorTableCounters', tableCounters);
 *   provide('editorFigureCounters', figureCounters);
 *
 * Usage in a NodeView:
 *   const tableCounters = inject<Readonly<Ref<ReadonlyMap<string, number>>>>('editorTableCounters');
 *   const number = computed(() => tableCounters?.value.get(props.node.attrs.tableId) ?? '?');
 */
export function useDocumentCounters(editor: Readonly<Ref<Editor | undefined>>) {
    // Map<tableId, displayNumber>
    const tableCounters = ref<ReadonlyMap<string, number>>(new Map());
    // Map<figureId, displayNumber>
    const figureCounters = ref<ReadonlyMap<string, number>>(new Map());

    let debounce: ReturnType<typeof setTimeout>;

    function scan() {
        if (!editor.value) {
            return;
        }

        const tableMap = new Map<string, number>();
        const figureMap = new Map<string, number>();
        let tn = 0;
        let fn = 0;
        editor.value.state.doc.descendants((node) => {
            if (
                node.type.name === 'numberedTable' ||
                node.type.name === 'extractionTable'
            ) {
                tn++;
                const id = node.attrs.tableId as string;

                if (id) {
                    tableMap.set(id, tn);
                }
            }

            if (node.type.name === 'figure') {
                fn++;
                const id = node.attrs.figureId as string;

                if (id) {
                    figureMap.set(id, fn);
                }
            }
        });
        tableCounters.value = tableMap;
        figureCounters.value = figureMap;
    }

    watch(
        editor,
        (e) => {
            if (!e) {
                return;
            }

            e.on('update', () => {
                clearTimeout(debounce);
                debounce = setTimeout(scan, 300);
            });
            // Initial scan after editor mounts
            scan();
        },
        { immediate: true },
    );

    return {
        tableCounters: readonly(tableCounters),
        figureCounters: readonly(figureCounters),
    };
}
